import React, {useContext, useRef} from 'react';
import {
  AccessibilityInfo,
  Platform,
  Pressable,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import BasePopover from '/component/base/Popover';
import Text from '/component/base/Text';
import {sanitize} from '/component/screen/report/VolumePerformanceReport/constants/volumePerformanceReport.constants';
import {PopoverContext} from '/provider/PopoverProvider/createPopoverProvider';
import {getTextFromReactNode} from '/util/accessibility.util';
import {useAccessibilityFocus} from '/util/hooks/useAccessibilityFocus.hook';

import {styles} from './InfoPopover.style';

export interface InfoPopoverItem {
  title: string | React.ReactNode;
  description: string | React.ReactNode;
}

export interface InfoPopoverProps {
  heading: React.ReactNode;
  body: React.ReactNode | InfoPopoverItem[];
  // eslint-disable-next-line react/require-default-props
  headingStyle?: any;
  // eslint-disable-next-line react/require-default-props
  titleStyle?: any;
  // eslint-disable-next-line react/require-default-props
  bodyStyle?: any;
}

const InfoPopover: React.FC<InfoPopoverProps> = ({
  heading = '',
  body = '',
  headingStyle = {},
  titleStyle = {},
  bodyStyle = {},
}) => {
  const {height: windowHeight} = useWindowDimensions();
  const fontResizeProps = {
    allowFontScaling: true,
    maxFontSizeMultiplier: 2,
  } as const;
  const maxBodyHeight = Math.max(180, windowHeight * 0.55);

  const {popoverHide} = useContext(PopoverContext) || {};
  const sectionRefs = useRef<{[key: string]: View | null}>({});
  const userInteracted = useRef(false);
  const announcementCancelled = useRef(false);
  const activeTimeouts = useRef<NodeJS.Timeout[]>([]);
  const closeButtonRef = useRef(null);
  const isScreenReaderEnabled = useAccessibilityFocus(closeButtonRef);

  const headingText =
    typeof heading === 'string' ? heading : getTextFromReactNode(heading);

  const handleUserInteraction = () => {
    userInteracted.current = true;
  };

  const handleClose = async () => {
    announcementCancelled.current = true;
    activeTimeouts.current.forEach(timeout => clearTimeout(timeout));
    activeTimeouts.current = [];

    await AccessibilityInfo.announceForAccessibility('Popover closed');
    popoverHide?.();
  };

  const renderBody = () => {
    if (!Array.isArray(body)) {
      return (
        <View
          ref={ref => (sectionRefs.current.body = ref)}
          accessible
          focusable>
          <Text
            style={[styles.popoverTextBody, bodyStyle]}
            {...fontResizeProps}>
            {body}
          </Text>
        </View>
      );
    }

    return body.map((item, index) => {
      const hasTitle = !!item.title;
      const titleText =
        typeof item.title === 'string'
          ? item.title
          : getTextFromReactNode(item.title);

      return (
        <View key={index} style={styles.sectionBlock}>
          {hasTitle && (
            <View
              ref={ref => (sectionRefs.current[`title-${index}`] = ref)}
              accessible
              focusable
              accessibilityRole="header"
              accessibilityLabel={titleText}
              onAccessibilityTap={handleUserInteraction}>
              <Text
                style={[styles.popoverTextTitle, titleStyle]}
                {...fontResizeProps}>
                {titleText}
              </Text>
            </View>
          )}
          <View
            ref={ref => (sectionRefs.current[`desc-${index}`] = ref)}
            accessible
            focusable
            accessibilityRole="text"
            onAccessibilityTap={handleUserInteraction}>
            <Text
              style={[
                styles.popoverTextBody,
                hasTitle ? styles.sectionDescriptionWithTitle : null,
                bodyStyle,
              ]}
              {...fontResizeProps}>
              {item.description}
            </Text>
          </View>
        </View>
      );
    });
  };

  return (
    <BasePopover padded>
      <View
        style={styles.base}
        accessibilityViewIsModal
        importantForAccessibility="yes"
        focusable>
        <View style={styles.headerRow} accessibilityRole="header">
          {/* <View accessible={Platform.OS == 'ios' ? false : !isScreenReaderEnabled}>
            <CloseButton
              viewRef={closeButtonRef}
              ref={closeButtonRef}
              style={styles.iconContainer}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel="Close"
              onPress={popoverClose}>
              <Icon name="close" style={styles.closeIcon} />
            </CloseButton>
          </View> */}

          {Platform.OS == 'ios' ? (
            <View>
              <KeyboardFocusable
                viewRef={closeButtonRef}
                style={styles.iconContainer}
                accessible
                accessibilityRole="button"
                accessibilityLabel="Close"
                onPress={handleClose}>
                <Icon name="close" style={styles.closeIcon} />
              </KeyboardFocusable>
            </View>
          ) : (
            <View accessible={!isScreenReaderEnabled}>
              <Pressable
                ref={closeButtonRef}
                style={styles.iconContainer}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel="Close"
                onPress={handleClose}>
                <Icon name="close" style={styles.closeIcon} />
              </Pressable>
            </View>
          )}

          <View
            ref={ref => (sectionRefs.current.heading = ref)}
            style={styles.headingContainer}
            testID={`${sanitize(headingText)}-popover`}
            accessible
            focusable
            accessibilityRole="header">
            <Text
              style={[
                Platform.OS === 'web'
                  ? styles.popoverHeadingText
                  : styles.popoverTextTitle,
                headingStyle,
              ]}
              {...fontResizeProps}>
              {headingText}
            </Text>
          </View>
        </View>

        <View style={styles.divider} />
        <View style={styles.body} testID="popover-body">
          <ScrollView
            contentContainerStyle={styles.bodyContent}
            style={[styles.bodyScroll, {maxHeight: maxBodyHeight}]}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator>
            {renderBody()}
          </ScrollView>
        </View>
      </View>
    </BasePopover>
  );
};

export default InfoPopover;
